import { useAllPlayersSessionSummary } from '@frontend/react'
import { useCallback, useMemo } from 'react'
import type { TabDef } from '../../../lib/sport-config'
import {
  buildEventDataByPlayer,
  buildPresenceByPlayer,
  hasTabData
} from './player-availability'

/**
 * Derives per-tab player availability from the all-players session summary's
 * per-player `metricsPresent` signal.
 *
 * One fetch returns presence for every player in the session, so the player
 * carousel and tab strip can be filtered up front — unlike the per-player
 * summary (`usePlayerSessionSummary`) fetched one-at-a-time on selection.
 *
 * Returns `hasTabData(playerId, tab)`; the caller composes it with its own
 * position filter and sort order to derive the visible players and tabs (so
 * this hook stays agnostic of position filtering and never-blank fallbacks).
 *
 * Degrades gracefully when the BE has not shipped `metricsPresent`: every
 * player is treated as available, so the carousel never wrongly empties. See
 * `player-availability.ts` for the fallback rules.
 *
 * `hasSummaryData` reports whether the all-players summary returned any player
 * at all. When it has settled empty, no player has data for the session — the
 * caller uses this to show an empty state rather than the full roster with
 * empty tables (an empty array carries no presence to filter on either way).
 *
 * Surfaces `isFetching` (any request in flight, including a session-switch
 * refetch of already-cached data) rather than `isLoading` (first-ever fetch
 * only): the carousel skeleton must gate on `isFetching`, because on a session
 * switch `isLoading` is false and gating on it would flash the previous
 * session's roster before presence filtering re-settles.
 */
export const usePlayerAvailability = (teamId: string, sessionId: string) => {
  const { playerStats, isFetching } = useAllPlayersSessionSummary(
    teamId,
    sessionId
  )

  const presenceByPlayer = useMemo(
    () => buildPresenceByPlayer(playerStats),
    [playerStats]
  )

  const eventDataByPlayer = useMemo(
    () => buildEventDataByPlayer(playerStats),
    [playerStats]
  )

  const playerHasTabData = useCallback(
    (playerId: string, tab: TabDef) =>
      hasTabData(presenceByPlayer, playerId, tab),
    [presenceByPlayer]
  )

  const playerHasEventData = useCallback(
    (playerId: string) => !!eventDataByPlayer[playerId],
    [eventDataByPlayer]
  )

  return {
    // Exposed for the DEV-only presence-key drift check in player-detail (the
    // caller composes it with the sport-config tabs); not used in prod render.
    presenceByPlayer,
    hasTabData: playerHasTabData,
    // Coarse, session-wide "has raw event data" — see buildEventDataByPlayer.
    // Only meant to keep a player who has *some* data from making the whole
    // group look dead in `noGroupData`; not a per-tab signal.
    hasEventData: playerHasEventData,
    hasSummaryData: playerStats.length > 0,
    isFetching
  }
}
